import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  IconInquiry,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

// Modern Inbox Icon - clean transparent style with thicker lines
const InboxSVG = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path 
      d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    <path 
      d="m3 7 9 6 9-6" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

// Modern Profile Icon - clean transparent style with thicker lines
const ProfileSVG = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <circle 
      cx="12" 
      cy="7" 
      r="4" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    <path 
      d="M5.5 21a7.5 7.5 0 0 1 13 0" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? (
    <span className={css.notificationDot} />
  ) : null;
  return (
    <NamedLink
      className={css.topbarIconButton}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
      aria-label="Inbox"
    >
      <InboxSVG className={css.inboxSvg} />
      {notificationDot}
    </NamedLink>
  );
};

const ProfileMenu = ({ currentUser, onLogout, intl }) => {
  const currentPageClass = (page) => {
    return page === 'ProfileSettingsPage' ? css.currentPage : null;
  };

  return (
    <Menu
      className={css.profileMenu}
      contentPlacementOffset={0}
      contentPosition="left"
      useArrow={false}
    >
      <MenuLabel className={css.profileMenuLabel}>
        <ProfileSVG className={css.profileSvg} />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="profile-settings">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <button
            className={css.logoutButton}
            onClick={onLogout}
            type="button"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </button>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Object} props.intl
 * @param {Object} props.config
 * @returns {JSX.Element} topbar component
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,    intl,
    isAuthenticated,
    onLogout,
  } = props;const [mounted, setMounted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;
  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentUser={currentUser} onLogout={onLogout} intl={intl} />
  ) : null;

  // Modern action buttons for desktop
  const desktopActionButtons = authenticatedOnClientSide ? (
    <>
      {inboxLinkMaybe}
      {profileMenuMaybe}
    </>
  ) : (
    <>
      <NamedLink name="LoginPage" className={css.signInButton}>
        <FormattedMessage id="TopbarDesktop.login" />
      </NamedLink>
      <NamedLink name="SignupPage" className={css.hireUsButton}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </NamedLink>
    </>
  );  return (
    <header className={classes}>
      <div className={css.container}>
        {/* Logo Section */}
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {/* Navigation Section */}
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        />

        {/* Action Buttons Section */}
        {desktopActionButtons}

        {/* Mobile Menu Button */}
        <button 
          className={css.mobileMenuButton}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          <span className={css.hamburgerLine}></span>
          <span className={css.hamburgerLine}></span>
          <span className={css.hamburgerLine}></span>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className={css.mobileMenu}>
          <div className={css.mobileMenuContent}>
            <div className={css.mobileNavigation}>
              <CustomLinksMenu
                currentPage={currentPage}
                customLinks={customLinks}
                intl={intl}
                hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
              />
            </div>
            <div className={css.mobileActionButtons}>
              {authenticatedOnClientSide ? (
                <>
                  {inboxLinkMaybe}
                  {profileMenuMaybe}
                </>
              ) : (
                <>
                  <NamedLink name="LoginPage" className={css.mobileSignInButton}>
                    <FormattedMessage id="TopbarDesktop.login" />
                  </NamedLink>
                  <NamedLink name="SignupPage" className={css.mobileHireUsButton}>
                    <FormattedMessage id="TopbarDesktop.signup" />
                  </NamedLink>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopbarDesktop;
